// Gestionnaire de panier avec protection contre les boucles infinies
class CartManager {
  constructor() {
    if (window.cartManager) {
      console.warn('CartManager déjà initialisé, utilisation de l\'instance existante');
      return window.cartManager;
    }

    // Réinitialiser l'instance précédente si elle existe
    if (window.cartManager) {
      window.cartManager.clearQueue();
    }

    this.queue = [];
    this.isProcessing = false;
    this.minRequestInterval = 2000;
    this.lastRequestTime = 0;
    this.retryDelay = 5000;
    this.maxRetries = 3;
    this.cartEndpoint = window.Shopify ? window.Shopify.routes.root + 'cart/add.js' : '/cart/add.js';
    this.processingTimeout = null;
    this.maxQueueSize = 10;
    this.lastProductId = null;
    this.lastAddTime = 0;
    this.initialized = false;

    // Stocker l'instance dans window
    window.cartManager = this;
    console.log('Nouvelle instance CartManager créée');
  }

  initialize() {
    if (this.initialized) {
      return;
    }

    // S'assurer que nous n'avons qu'une seule instance
    if (window.cartManager && window.cartManager !== this) {
      window.cartManager.clearQueue();
    }

    window.cartManager = this;
    this.initialized = true;
    console.log('CartManager initialisé avec succès', {
      cartEndpoint: this.cartEndpoint
    });
  }

  async addToCart(productId, quantity = 1, properties = {}) {
    if (!this.initialized) {
      this.initialize();
    }

    // Protection contre les clics rapides multiples
    const now = Date.now();
    if (productId === this.lastProductId && now - this.lastAddTime < 1000) {
      console.log('Ignorer le clic rapide répété');
      return Promise.reject(new Error('Veuillez attendre avant d\'ajouter à nouveau le même produit'));
    }

    // Vérifier le délai depuis la dernière requête
    const timeSinceLastRequest = now - this.lastRequestTime;
    
    if (timeSinceLastRequest < this.minRequestInterval) {
      console.log('Requête trop rapide, attente...');
      await new Promise(resolve => setTimeout(resolve, this.minRequestInterval - timeSinceLastRequest));
    }

    // Vérifier la taille de la file d'attente
    if (this.queue.length >= this.maxQueueSize) {
      console.log('File d\'attente pleine, nettoyage...');
      this.clearQueue();
      return Promise.reject(new Error('Trop de requêtes en attente, veuillez réessayer'));
    }

    this.lastProductId = productId;
    this.lastAddTime = now;

    return new Promise((resolve, reject) => {
      // Ajouter la requête à la file d'attente
      this.queue.push({
        productId,
        quantity,
        properties,
        resolve,
        reject,
        retries: 0,
        addedTime: now
      });
      
      if (!this.isProcessing) {
        this.processQueue();
      }
    });
  }

  async processQueue() {
    // Protection contre les boucles infinies
    if (this.processingTimeout) {
      clearTimeout(this.processingTimeout);
      this.processingTimeout = null;
    }

    if (this.queue.length === 0) {
      this.isProcessing = false;
      return;
    }

    this.isProcessing = true;
    const currentTime = Date.now();
    const timeToWait = Math.max(0, this.lastRequestTime + this.minRequestInterval - currentTime);

    // Nettoyer les anciennes requêtes
    this.queue = this.queue.filter(item => currentTime - item.addedTime < 30000);

    if (this.queue.length === 0) {
      this.isProcessing = false;
      return;
    }

    await new Promise(resolve => setTimeout(resolve, timeToWait));

    const request = this.queue[0];

    try {
      console.log('Tentative d\'ajout au panier...', request.productId);
      const response = await fetch(this.cartEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          items: [{
            id: request.productId,
            quantity: request.quantity,
            properties: request.properties
          }]
        })
      });

      this.lastRequestTime = Date.now();

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Produit ajouté avec succès', data);
      request.resolve(data);
      this.queue.shift();
    } catch (error) {
      console.error('Erreur lors de l\'ajout au panier:', error);

      if (request.retries < this.maxRetries) {
        request.retries++;
        console.log(`Nouvelle tentative ${request.retries}/${this.maxRetries} dans ${this.retryDelay}ms`);
        await new Promise(resolve => setTimeout(resolve, this.retryDelay));
        this.isProcessing = false;
        this.processQueue();
        return;
      }

      this.queue.shift();
      request.reject(error);
    }

    // Planifier le traitement de la prochaine requête
    this.isProcessing = false;
    if (this.queue.length > 0) {
      this.processingTimeout = setTimeout(() => {
        if (this.queue.length > 0) {
          this.processQueue();
        }
      }, this.minRequestInterval);
    }
  }

  clearQueue() {
    // Arrêter tout traitement en cours
    if (this.processingTimeout) {
      clearTimeout(this.processingTimeout);
      this.processingTimeout = null;
    }

    // Rejeter toutes les requêtes en attente
    this.queue.forEach(item => {
      item.reject(new Error('File d\'attente nettoyée'));
    });

    // Réinitialiser l'état
    this.queue = [];
    this.isProcessing = false;
    this.lastProductId = null;
    this.lastAddTime = 0;
    this.lastRequestTime = 0;
  }
}

// Créer une nouvelle instance et l'initialiser
const cartManager = new CartManager();
cartManager.initialize();

// Exporter l'instance du gestionnaire de panier
window.cartManager = cartManager;
